import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, BookHeart, Check, Leaf, LifeBuoy, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import { BREATH_CYCLE_MS, BREATH_PHASES, personalizedPrompt } from "@/lib/zenPrompts";
import { getProtectItems, logMoment } from "@/lib/storage";
import { askReflection, reflectionCompanionAvailable } from "@/lib/aiReflect";
import { ZEN, ZEN_REFLECT } from "@/constants/testIds";

const DURATIONS = [2, 5, 10];

export default function Zen() {
  const nav = useNavigate();
  const protectItems = useMemo(() => getProtectItems(), []);
  const [selectedMinutes, setSelectedMinutes] = useState(2);
  const [customMinutes, setCustomMinutes] = useState(15);
  const [customOpen, setCustomOpen] = useState(false);
  const [delaySec, setDelaySec] = useState(0);
  const [delayActive, setDelayActive] = useState(false);
  const [delayDone, setDelayDone] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [prompt, setPrompt] = useState(() => personalizedPrompt(protectItems));
  const lastPromptRef = useRef(prompt);

  const [reflectOpen, setReflectOpen] = useState(false);
  const [reflectText, setReflectText] = useState("");
  const [reflectLoading, setReflectLoading] = useState(false);
  const [reflectResponse, setReflectResponse] = useState("");

  useEffect(() => {
    let phase = 0;
    let timeoutId;
    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        phase = (phase + 1) % BREATH_PHASES.length;
        setPhaseIdx(phase);
        schedule();
      }, BREATH_PHASES[phase].duration * 1000);
    };
    schedule();
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      const next = personalizedPrompt(protectItems, lastPromptRef.current);
      lastPromptRef.current = next;
      setPrompt(next);
    }, BREATH_CYCLE_MS);
    return () => window.clearInterval(id);
  }, [protectItems]);

  useEffect(() => {
    if (!delayActive) return undefined;
    if (delaySec <= 0) {
      setDelayActive(false);
      setDelayDone(true);
      logMoment("interrupted");
      toast.success("Pause complete.", {
        description: "You made a little room before the next moment.",
      });
      return undefined;
    }
    const id = window.setTimeout(() => setDelaySec((value) => value - 1), 1000);
    return () => window.clearTimeout(id);
  }, [delayActive, delaySec]);

  const actualMinutes = customOpen
    ? Math.min(120, Math.max(1, Number(customMinutes) || 1))
    : selectedMinutes;

  const startDelay = () => {
    setDelayDone(false);
    setDelaySec(actualMinutes * 60);
    setDelayActive(true);
  };

  const cancelDelay = () => {
    setDelayActive(false);
    setDelaySec(0);
  };

  const handleReflect = async () => {
    if (!reflectText.trim() || reflectLoading) return;
    setReflectLoading(true);
    setReflectResponse("");
    try {
      const { response } = await askReflection({
        text: reflectText.trim(),
        mode: "zen",
        protectList: protectItems.map((item) => item.label),
      });
      setReflectResponse(response);
    } catch (error) {
      toast(error?.userMessage || error?.response?.data?.detail || "The companion couldn't reach you just now.", {
        description: "Try again in a moment.",
      });
    } finally {
      setReflectLoading(false);
    }
  };

  const closeReflect = () => {
    setReflectOpen(false);
    setReflectText("");
    setReflectResponse("");
    setReflectLoading(false);
  };

  const mm = String(Math.floor(delaySec / 60)).padStart(2, "0");
  const ss = String(delaySec % 60).padStart(2, "0");
  const currentPhase = BREATH_PHASES[phaseIdx];

  return (
    <PageContainer testid={ZEN.root} className="pt-7">
      <BrandHeader compact />

      <section className="mb-5">
        <p
          className="text-[10px] uppercase tracking-[0.34em] mb-2"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Zen Mode
        </p>
        <h1
          className="text-[45px] leading-[0.98] tracking-[-0.035em]"
          style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
        >
          Pause. Protect
          <br />
          what matters.
        </h1>
        <p
          className="mt-2 italic text-[34px] leading-none"
          style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
        >
          Reach before the spiral.
        </p>
      </section>

      <div className="relative h-[330px] -mx-5 overflow-hidden">
        <div
          className="absolute left-1/2 top-5 -translate-x-1/2 w-64 h-64 rounded-full"
          style={{ background: "radial-gradient(circle, #fff2d5 0%, rgba(214,184,132,0.30) 48%, transparent 70%)" }}
        />
        <motion.div
          data-testid={ZEN.breathCircle}
          aria-label={currentPhase.label}
          className="absolute z-10 left-1/2 top-[74px] -translate-x-1/2 w-36 h-36 rounded-full"
          style={{
            background: "radial-gradient(circle at 38% 30%, #fff6df 0%, #e3c3b0 58%, #d7aeba 100%)",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.70), 0 0 44px rgba(214,184,132,0.28)",
          }}
          animate={{ scale: [0.9, 1.12, 1.12, 0.9, 0.9] }}
          transition={{ duration: BREATH_CYCLE_MS / 1000, times: [0, 0.25, 0.5, 0.75, 1], repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute left-[-10%] right-[-10%] top-[190px] h-40 rounded-[50%] rotate-3" style={{ background: "rgba(224,200,208,0.54)" }} />
        <div className="absolute left-[-10%] right-[-10%] top-[230px] h-40 rounded-[50%] -rotate-3" style={{ background: "rgba(196,151,172,0.34)" }} />
        <div className="absolute z-20 inset-x-6 bottom-2 text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={delayActive ? `${mm}:${ss}` : prompt}
              data-testid={delayActive ? ZEN.delayCountdown : ZEN.prompt}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-[24px] leading-tight"
              style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
            >
              {delayActive ? `${mm}:${ss}` : "Take a deep breath."}
            </motion.p>
          </AnimatePresence>
          <p
            data-testid={ZEN.phase}
            className="mt-1 italic text-[17px]"
            style={{ color: "var(--un-text-2)", fontFamily: "Cormorant Garamond, serif" }}
          >
            {delayActive ? currentPhase.label : prompt}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-2">
        {DURATIONS.map((minutes) => {
          const active = !customOpen && selectedMinutes === minutes;
          return (
            <button
              key={minutes}
              data-testid={`${ZEN.durationPrefix}${minutes}`}
              onClick={() => {
                setSelectedMinutes(minutes);
                setCustomOpen(false);
              }}
              className="py-3 rounded-full text-[13px] un-press"
              style={{
                background: active ? "var(--un-plum)" : "rgba(91,57,84,0.055)",
                color: active ? "#fffaf5" : "var(--un-plum-deep)",
                boxShadow: active ? "0 8px 20px rgba(74,45,72,0.15)" : "none",
              }}
            >
              {minutes} min
            </button>
          );
        })}
        <button
          data-testid={`${ZEN.durationPrefix}custom`}
          onClick={() => setCustomOpen(true)}
          className="py-3 rounded-full text-[13px] un-press"
          style={{
            background: customOpen ? "var(--un-plum)" : "rgba(91,57,84,0.055)",
            color: customOpen ? "#fffaf5" : "var(--un-plum-deep)",
          }}
        >
          Custom
        </button>
      </div>

      <AnimatePresence>
        {customOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <label className="flex items-center justify-between mt-3 rounded-2xl px-4 py-3" style={{ background: "rgba(91,57,84,0.05)" }}>
              <span className="text-[13px]" style={{ color: "var(--un-text-2)" }}>Pause length</span>
              <span className="flex items-center gap-2">
                <input
                  data-testid={ZEN.customDuration}
                  type="number"
                  min="1"
                  max="120"
                  value={customMinutes}
                  onChange={(event) => setCustomMinutes(event.target.value)}
                  className="w-16 rounded-xl px-2 py-1 text-center outline-none"
                  style={{ background: "#fffaf5", color: "var(--un-plum-deep)" }}
                />
                <span className="text-[12px]" style={{ color: "var(--un-text-3)" }}>minutes</span>
              </span>
            </label>
          </motion.div>
        )}
      </AnimatePresence>

      {delayDone && (
        <div className="mt-4 rounded-2xl p-4 flex items-center gap-3" style={{ background: "rgba(74,107,93,0.10)", color: "var(--un-safe)" }}>
          <Check size={18} />
          <span className="text-[14px]">Pause complete.</span>
        </div>
      )}

      <button
        data-testid={ZEN.startDelay}
        onClick={delayActive ? cancelDelay : startDelay}
        className="w-full mt-4 py-4 rounded-full text-[16px] font-medium flex items-center justify-center gap-3 un-press"
        style={{
          background: delayActive ? "rgba(91,57,84,0.09)" : "linear-gradient(105deg, #4A2D48, #6D4866)",
          color: delayActive ? "var(--un-plum)" : "#fffaf5",
          boxShadow: delayActive ? "none" : "0 14px 30px rgba(74,45,72,0.20)",
        }}
      >
        {delayActive ? "End this pause" : "Start a pause"}
        {!delayActive && <ArrowRight size={18} />}
      </button>

      {reflectionCompanionAvailable && (
      <button
        onClick={() => setReflectOpen(true)}
        data-testid={ZEN_REFLECT.openBtn}
        className="w-full mt-4 rounded-[1.75rem] p-5 text-left flex items-center gap-4 un-press"
        style={{ background: "linear-gradient(135deg, #F2E8DE, #EBDADC)" }}
      >
        <Leaf size={24} style={{ color: "var(--un-plum)" }} />
        <span className="flex-1">
          <span className="block text-[18px]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>A little more room</span>
          <span className="block italic text-[14px]" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>A small pause can make the next moment easier to navigate.</span>
        </span>
        <Sparkles size={16} style={{ color: "var(--un-burgundy)" }} />
      </button>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3 mb-8">
        <button onClick={() => nav("/journal")} className="rounded-3xl p-4 text-left un-press" style={{ background: "rgba(91,57,84,0.05)" }}>
          <BookHeart size={18} style={{ color: "var(--un-plum)" }} />
          <span className="block mt-2 text-[13px]" style={{ color: "var(--un-text-1)" }}>Write privately</span>
        </button>
        <button data-testid={ZEN.goSafereach} onClick={() => nav("/safereach")} className="rounded-3xl p-4 text-left un-press" style={{ background: "rgba(74,107,93,0.10)" }}>
          <LifeBuoy size={18} style={{ color: "var(--un-safe)" }} />
          <span className="block mt-2 text-[13px]" style={{ color: "var(--un-text-1)" }}>Reach support</span>
        </button>
      </div>

      {reflectionCompanionAvailable && (
      <AnimatePresence>
        {reflectOpen && (
          <motion.div
            data-testid={ZEN_REFLECT.panel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end justify-center"
            style={{ background: "rgba(42,27,42,0.42)", backdropFilter: "blur(6px)" }}
            onClick={closeReflect}
          >
            <motion.div
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-[480px] rounded-t-[2rem] p-6 un-safe-bottom"
              style={{ background: "var(--un-bg-1)", boxShadow: "0 -20px 60px rgba(42,27,42,0.24)" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles size={15} style={{ color: "var(--un-burgundy)" }} />
                  <p className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--un-burgundy)" }}>Reflection</p>
                </div>
                <button data-testid={ZEN_REFLECT.closeBtn} onClick={closeReflect} aria-label="Close" className="w-9 h-9 rounded-full flex items-center justify-center un-press" style={{ background: "rgba(91,57,84,0.06)" }}>
                  <X size={15} />
                </button>
              </div>

              {reflectResponse ? (
                <div data-testid={ZEN_REFLECT.response} className="rounded-2xl p-5 mb-3" style={{ background: "linear-gradient(135deg, #F3E9DF, #EBDADC)" }}>
                  <p className="text-[18px] leading-relaxed italic whitespace-pre-wrap" style={{ color: "var(--un-text-1)", fontFamily: "Cormorant Garamond, serif" }}>{reflectResponse}</p>
                </div>
              ) : (
                <>
                  <p className="text-[15px] italic mb-2" style={{ color: "var(--un-text-2)", fontFamily: "Cormorant Garamond, serif" }}>What’s true right now? One sentence is enough.</p>
                  <p className="text-[11px] mb-3" style={{ color: "var(--un-text-3)" }}>This line and your Protect List labels are sent for AI processing. A random app-install identifier is sent to the UnAnonymous backend for temporary abuse limits. See Privacy for details.</p>
                  <textarea
                    data-testid={ZEN_REFLECT.textarea}
                    value={reflectText}
                    onChange={(event) => setReflectText(event.target.value)}
                    rows={4}
                    autoFocus
                    placeholder="Just one honest line."
                    className="w-full resize-none outline-none rounded-2xl p-4 text-[15px] mb-3"
                    style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
                  />
                </>
              )}

              <button
                data-testid={ZEN_REFLECT.submit}
                onClick={reflectResponse ? closeReflect : handleReflect}
                disabled={!reflectResponse && (!reflectText.trim() || reflectLoading)}
                className="w-full py-3.5 rounded-full text-[14px] font-medium un-press disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(105deg, #4A2D48, #6D4866)", color: "#fffaf5" }}
              >
                {reflectLoading ? "Listening…" : reflectResponse ? "Thank the companion" : "Reflect"}
                {!reflectLoading && !reflectResponse && <ArrowRight size={14} />}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      )}
    </PageContainer>
  );
}
